import { IUpdateNcm } from "@/@types/ncm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { useGetNcmByIdQuery, useUpdateNcmMutation } from "@/services/ncm";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { FaChevronLeft, FaSave } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { formatTaxValue, UF_OPTIONS } from "../create/utils";
import { FormSchema } from "../create/validation-forms";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";

export const EditNcm: React.FC = () => {
  const navigate = useNavigate();
  const { id_ncm } = useParams<{ id_ncm: string }>();

  const { data: ncmResponse, isLoading } = useGetNcmByIdQuery(Number(id_ncm));
  const [updateNcm, { isLoading: isUpdating }] = useUpdateNcmMutation();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      ncm: "",
      state: "",
      iva: "",
      internal_tax_rate: "",
      interstate_tax: "",
      is_active: true,
    },
  });

  useEffect(() => {
    if (ncmResponse?.data) {
      const { ncm, state, iva, internal_tax_rate, interstate_tax, is_active } =
        ncmResponse.data;

      setValue("ncm", ncm || "");
      setValue("state", state || "");
      setValue("iva", iva ? iva.replace(".", ",") : "");
      setValue(
        "internal_tax_rate",
        internal_tax_rate ? internal_tax_rate.replace(".", ",") : "",
      );
      setValue(
        "interstate_tax",
        interstate_tax ? interstate_tax.replace(".", ",") : "",
      );
      setValue("is_active", is_active ?? true);
    }
  }, [ncmResponse, setValue]);

  const onSubmit = async (data: IUpdateNcm) => {
    const formattedData = {
      ...data,
      iva: data.iva.replace(",", "."),
      internal_tax_rate: data.internal_tax_rate.replace(",", "."),
      interstate_tax: data.interstate_tax.replace(",", "."),
    };

    try {
      await updateNcm({ id: Number(id_ncm), body: formattedData }).unwrap();
      toast({ variant: "success", description: "NCM atualizado com sucesso!" });
      navigate(`/fiscal/ncm`);
    } catch (error) {
      toast({ variant: "destructive", description: "Erro ao atualizar NCM." });
    }
  };

  return (
    <PageLayout>
      <ContentContainer>
        <div className="flex flex-col w-full">
          <div className="w-full max-w-3xl">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-lg text-gray-700"
            >
              <FaChevronLeft className="mr-2" />
              Voltar
            </button>
          </div>
          <PageTitle
            title="Fiscal"
            description="Editar NCM"
            className="max-w-3xl"
          />

          <Card className="w-full max-w-3xl p-6 rounded-lg shadow-md mt-5">
            {isLoading ? (
              <p className="text-center text-lg">Carregando...</p>
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="grid grid-cols-2 gap-6"
              >
                <div className="col-span-2">
                  <Label>NCM:</Label>
                  <Input
                    {...register("ncm")}
                    hasError={!!errors.ncm}
                    placeholder="Digite o NCM"
                  />
                </div>

                <div>
                  <Label>UF:</Label>
                  <Controller
                    control={control}
                    name="state"
                    render={({ field }) => (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {UF_OPTIONS.map((uf) => (
                            <SelectItem key={uf} value={uf}>
                              {uf}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div>
                  <Label>Status:</Label>
                  <Controller
                    control={control}
                    name="is_active"
                    render={({ field }) => (
                      <Select
                        value={String(field.value)}
                        onValueChange={(value) =>
                          field.onChange(value === "true")
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Ativo</SelectItem>
                          <SelectItem value="false">Inativo</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div>
                  <Label>IVA:</Label>
                  <Input
                    {...register("iva")}
                    hasError={!!errors.iva}
                    placeholder="IVA (%)"
                    onChange={(e) =>
                      setValue("iva", formatTaxValue(e.target.value))
                    }
                  />
                </div>

                <div>
                  <Label>Taxa de Imposto Interno:</Label>
                  <Input
                    {...register("internal_tax_rate")}
                    hasError={!!errors.internal_tax_rate}
                    placeholder="Taxa de imposto interno (%)"
                    onChange={(e) =>
                      setValue(
                        "internal_tax_rate",
                        formatTaxValue(e.target.value),
                      )
                    }
                  />
                </div>

                <div>
                  <Label>Taxa Interestadual:</Label>
                  <Input
                    {...register("interstate_tax")}
                    hasError={!!errors.interstate_tax}
                    placeholder="Taxa interestadual (%)"
                    onChange={(e) =>
                      setValue("interstate_tax", formatTaxValue(e.target.value))
                    }
                  />
                </div>

                <div className="col-span-2 flex justify-end">
                  <Button type="submit" disabled={isUpdating}>
                    <FaSave />
                    {isUpdating ? "Salvando..." : "Salvar Alterações"}
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>
      </ContentContainer>
    </PageLayout>
  );
};

export default EditNcm;
